import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactCodeInput from 'react-verification-code-input';
import Modal from '@mui/material/Modal';
import QRCode from 'qrcode';

import * as Styled from '../LoyaltyPageSeparated.styles';
import { selectors } from '../../LoyaltyPage/reducer';
import { selectors as selectorsLogin } from '../../LoginPage/reducer';
import * as actions from '../../LoyaltyPage/actions';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';
import type { BonusHelpState } from '../../LoyaltyPageV3/components/loyaltyHelpContent';

export interface StandartReceiptItem {
  ware_id: string;
  price: number;
  quantity: number;
}

export interface ConfirmedLoyaltyAmounts {
  payable: number;
  bonusDebit: number;
}

interface LoyaltyApplyScreenProps {
  setShowWithBonuses: (val: boolean) => void;
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  onOrderConfirmed?: (
    standartItems?: StandartReceiptItem[],
    couponCheckout?: boolean,
    amounts?: ConfirmedLoyaltyAmounts
  ) => void;
  isPartnersPayment: boolean;
  variant?: 'legacy' | 'v3';
  onBackToReceipt?: () => void;
  couponContent?: React.ReactNode;
  externalBlocked?: boolean;
  onHelpStateChange?: (state: BonusHelpState | null) => void;
}

const LoyaltyApplyScreen: React.FC<LoyaltyApplyScreenProps> = ({
  setShowWithBonuses,
  partnerPercent,
  isStadaBonusesCredit,
  onOrderConfirmed,
  isPartnersPayment,
  variant = 'legacy',
  onBackToReceipt,
  couponContent,
  externalBlocked = false,
  onHelpStateChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const dataUser = useSelector(selectorsLogin.userData);
  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const appliedBonusesLoading = useSelector(selectors.appliedBonusesLoading);
  const appliedBonusesError = useSelector(selectors.appliedBonusesError);
  const submitOrderError = useSelector(selectors.submitOrderError);
  const submitOrderResult = useSelector(selectors.submitOrderResult);
  const submitOrderLoading = useSelector(selectors.submitOrderLoading);
  const sendToStandartError = useSelector(selectors.sendToStandartError);

  const [value, setValue] = useState<string>('');
  const [qrSrc, setQrSrc] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');
  const [isQrModalOpen, setIsQrModalOpen] = useState<boolean>(false);
  const [showCodeInput, setShowCodeInput] = useState<boolean>(false);

  const clientBalance = (clientInfo && clientInfo.balance) ?? 0;
  const stadaBalance = (stadaInfo && stadaInfo.balance) ?? 0;

  // Есть ли в чеке штада-товары
  const hasStadaItems = useMemo(
    () => chosenReceipt?.items?.some((item) => item.stadaPercent > 0) ?? false,
    [chosenReceipt]
  );

  const currentItems = useMemo(
    () =>
      distributeClientBonus(
        chosenReceipt?.items ?? [],
        value?.length === 6 ? clientBalance : 0,
        partnerPercent,
        value?.length === 6 && hasStadaItems && isStadaBonusesCredit
          ? stadaBalance
          : 0
      ),
    [
      chosenReceipt,
      clientBalance,
      stadaBalance,
      hasStadaItems,
      isStadaBonusesCredit,
      value,
      partnerPercent,
    ]
  );

  const previewItems = useMemo(
    () =>
      distributeClientBonus(
        chosenReceipt?.items ?? [],
        clientBalance,
        partnerPercent,
        hasStadaItems && isStadaBonusesCredit ? stadaBalance : 0
      ),
    [
      chosenReceipt,
      clientBalance,
      partnerPercent,
      hasStadaItems,
      isStadaBonusesCredit,
      stadaBalance,
    ]
  );

  const totalBonuses = (
    bonuses: 'bonusesToClient' | 'bonusesToPharmacist' | 'stadaBonus'
  ) => currentItems?.reduce((acc, item) => acc + item[bonuses], 0) || 0;

  // Итоговые суммы для блока информации
  const totalPrice = useMemo(
    () =>
      (chosenReceipt?.items ?? []).reduce(
        (acc, item) => acc + Number(item?.total_price ?? 0),
        0
      ),
    [chosenReceipt]
  );

  const totalDebit = useMemo(
    () => currentItems.reduce((acc, item) => acc + item.appliedBonus, 0),
    [currentItems]
  );

  // Отдельно штадовское списание для отображения в строке OrderSummary
  const totalStadaDebit = useMemo(
    () =>
      currentItems.reduce(
        (acc, item) => acc + (item.appliedStadaBonus ?? 0),
        0
      ),
    [currentItems]
  );

  const bonusesToClient = useMemo(
    () => Math.floor(totalBonuses('bonusesToClient')),
    [currentItems]
  );

  const stadaBonus = useMemo(
    () => Math.floor(totalBonuses('stadaBonus')),
    [currentItems]
  );

  const finalPrice = useMemo(() => {
    const result = totalPrice - totalDebit;
    return result > 0 ? result : 0;
  }, [totalPrice, totalDebit]);

  const previewDebit = useMemo(
    () =>
      previewItems.reduce(
        (acc, item) => acc + Number(item.appliedBonus ?? 0),
        0
      ),
    [previewItems]
  );

  const previewFinalPrice = Math.max(totalPrice - previewDebit, 0);
  useEffect(() => {
    onHelpStateChange?.({
      useBonuses: showCodeInput,
      available: previewDebit > 0,
      error: !!appliedBonusesError,
    });
  }, [onHelpStateChange, showCodeInput, previewDebit, appliedBonusesError]);
  useEffect(() => () => onHelpStateChange?.(null), [onHelpStateChange]);
  const formatAmount = (amount: number) =>
    Number(amount || 0).toLocaleString('ru-RU');

  const handleCodeInputChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleApplyBonuses = () => {
    if (!clientInfo?.phone) return;
    dispatch(
      actions.setLoyaltyInfoBot.request({
        sourceCode: dataUser?.sourceCode,
        action: `Нажата кнопка применения бонусов, ожидается код от клиента`,
        networkName: dataUser?.networkName,
        dockId: chosenReceipt?.doc_id,
      })
    );
    setValue('');
    setShowCodeInput(true);
    setShowWithBonuses(true);

    // функционал запроса на бэк отключен
    // dispatch(actions.applyBonuses.request(clientInfo.phone));
  };

  // Подтверждение заказа
  const handleConfirmOrder = () => {
    if (externalBlocked) return;
    if (!clientInfo?.phone) return;
    const confirmCode = value?.length === 6 ? { confirm_code: value } : {};
    const items = distributeClientBonus(
      chosenReceipt?.items ?? [],
      value?.length === 6 ? clientBalance : 0,
      partnerPercent,
      value?.length === 6 && hasStadaItems && isStadaBonusesCredit
        ? stadaBalance
        : 0
    );

    // Keep the exact prices confirmed by the client. V3 has an extra payment
    // selection step, so recalculating them there from transient Redux state can
    // lose the applied bonus and send the original price to Standart.
    const standartItems: StandartReceiptItem[] = items.map((item) => ({
      ware_id: item.ware_id,
      price: item.unit_price - item.appliedBonus / item.part_quantity,
      quantity: item.part_quantity,
    }));

    // Keep the existing calculator's result for display. Fractional unit
    // prices can differ from total_price by less than a cent; that difference
    // is not a bonus debit and must not be inferred from the /scan payload.
    const bonusDebit = items.reduce((sum, item) => sum + item.appliedBonus, 0);
    const confirmedAmounts: ConfirmedLoyaltyAmounts = {
      payable: Math.max(
        items.reduce((sum, item) => sum + Number(item.total_price ?? 0), 0) -
          bonusDebit,
        0
      ),
      bonusDebit,
    };
    const notifyOrderConfirmed = () => {
      if (variant === 'v3') {
        onOrderConfirmed?.(standartItems, false, confirmedAmounts);
      } else {
        onOrderConfirmed?.(standartItems);
      }
    };

    const bonusesClient =
      items?.reduce((acc, item) => acc + item.bonusesToClient, 0) || 0;
    const bonusesPharmacist =
      items?.reduce((acc, item) => acc + item.bonusesToPharmacist, 0) || 0;
    const stadaBonusCredit = isStadaBonusesCredit
      ? {
          stada_bonus_credit:
            items?.reduce((acc, item) => acc + item.stadaBonus, 0) || 0,
        }
      : {};

    // Суммарное штадовское списание
    const stadaBonusDebit = isStadaBonusesCredit
      ? {
          stada_bonus_debit:
            items?.reduce(
              (acc, item) => acc + (item.appliedStadaBonus ?? 0),
              0
            ) || 0,
        }
      : {};

    dispatch(
      actions.setLoyaltyInfoBot.request({
        sourceCode: dataUser?.sourceCode,
        action: `Нажата кнопка "Подтвердить". Код для списания бонусов ${
          Object.values(confirmCode).length ? '' : 'НЕ '
        }ВВОДИЛСЯ`,
        networkName: dataUser?.networkName,
        dockId: chosenReceipt?.doc_id,
      })
    );

    dispatch(
      actions.submitLoyaltyOrder.request({
        ...confirmCode,
        phone: clientInfo.phone,
        bonus_used: value?.length === 6,
        doc_id: chosenReceipt?.doc_id,
        source_code: dataUser?.sourceCode,
        isPartner: isPartnersPayment,
        items: items.map((item) => ({
          ware_id: item.ware_id,
          price: item.base_price - item.appliedBonus / item.quantity,
          quantity: item.quantity,
          name: item.name,
        })),
        // client_bonus_debit — только аптечная часть, без штадовской
        client_bonus_debit: items?.reduce(
          (acc, item) =>
            acc + (item.appliedBonus - (item.appliedStadaBonus ?? 0)),
          0
        ),
        client_bonus_credit: bonusesClient,
        pharmacist_bonus_credit: bonusesPharmacist,
        ...stadaBonusCredit,
        ...stadaBonusDebit,
        postEffect: async () => {
          if (value?.length !== 6) {
            setQrSrc('');
            // Переходим на экран оплаты сразу (без QR в стандарт)
            notifyOrderConfirmed();
            return;
          }
          !isPartnersPayment &&
            dispatch(
              actions.sendToStandart.request({
                payment_type: 'in_place',
                order_number: 'PA',
                doc_id: chosenReceipt?.doc_id,
                items: standartItems,
              })
            );
          try {
            setQrError('');
            const payload = JSON.stringify({
              payment_type: 'in_place',
              order_number: 'PA',
              doc_id: chosenReceipt?.doc_id,
              items: standartItems,
            });
            const dataUrl = await QRCode.toDataURL(payload, {
              errorCorrectionLevel: 'M',
              margin: 1,
              scale: 4,
            });
            setQrSrc(dataUrl);
          } catch (e: any) {
            console.error('QR generation error:', e);
            setQrSrc('');
            setQrError(e?.message || t('LoyaltySeparated.QR_ERROR'));
          }
          // Переход на экран оплаты после всего
          notifyOrderConfirmed();
        },
      })
    );
  };

  // Сброс при закрытии чека
  useEffect(() => {
    if (!chosenReceipt) {
      setQrError('');
      setQrSrc('');
      setValue('');
      setShowWithBonuses(true);
      setIsQrModalOpen(false);
      setShowCodeInput(false);
      dispatch(actions.resetApplyBonuses());
      dispatch(actions.resetSubmitOrder());
    }
  }, [chosenReceipt]);

  if (!clientInfo || !clientInfo?.phone) {
    return (
      <Styled.ApplyBlock>
        <Styled.BlockHint style={{ textAlign: 'center' }}>
          {t('LoyaltySeparated.APPLY_NO_CLIENT')}
        </Styled.BlockHint>
      </Styled.ApplyBlock>
    );
  }

  if (!chosenReceipt) {
    return (
      <Styled.ApplyBlock>
        <Styled.BlockHint style={{ textAlign: 'center' }}>
          {t('LoyaltySeparated.APPLY_NO_RECEIPT')}
        </Styled.BlockHint>
      </Styled.ApplyBlock>
    );
  }

  if (variant === 'v3') {
    const handleBackFromCode = () => {
      setValue('');
      setShowCodeInput(false);
      setShowWithBonuses(false);
      dispatch(actions.resetApplyBonuses());
    };

    return (
      <Styled.V3BonusScreen>
        <Styled.V3BonusContent>
          <Styled.V3BackButton
            type="button"
            onClick={showCodeInput ? handleBackFromCode : onBackToReceipt}
          >
            ←{' '}
            {showCodeInput
              ? t('LoyaltyV3.BACK_TO_BONUS_CHOICE')
              : t('LoyaltyV3.BACK_TO_RECEIPT')}
          </Styled.V3BackButton>

          <Styled.V3BonusTitle>
            {showCodeInput
              ? t('LoyaltyV3.CONFIRM_BONUS_DEBIT')
              : t('LoyaltyV3.USE_PHARMACY_BONUSES')}
          </Styled.V3BonusTitle>
          <Styled.V3BonusSubtitle>
            {showCodeInput
              ? t('LoyaltyV3.ASK_FOR_BONUS_CODE')
              : t('LoyaltyV3.ASK_CLIENT_BEFORE_CONTINUE')}
          </Styled.V3BonusSubtitle>

          <Styled.V3BonusOffer>
            <span>
              {showCodeInput
                ? t('LoyaltyV3.WILL_USE_AFTER_CONFIRMATION')
                : t('LoyaltyV3.AVAILABLE_FOR_RECEIPT')}
            </span>
            <strong>
              {formatAmount(previewDebit)} {t('LoyaltyV3.PHARMACY_BONUSES')}
            </strong>
          </Styled.V3BonusOffer>

          {!showCodeInput && (
            <Styled.V3BonusLimitHint>
              {t('LoyaltyV3.BONUS_LIMIT_HINT')}
            </Styled.V3BonusLimitHint>
          )}

          {showCodeInput && (
            <Styled.V3CodeField>
              <label>{t('LoyaltyV3.BONUS_CONFIRMATION_CODE')}</label>
              <ReactCodeInput
                fields={6}
                fieldWidth={44}
                fieldHeight={48}
                placeholder={['0', '0', '0', '0', '0', '0']}
                values={[...value]}
                onChange={handleCodeInputChange}
              />
              <small>{t('LoyaltyV3.BONUS_CODE_HINT')}</small>
            </Styled.V3CodeField>
          )}

          <Styled.V3BonusTotals>
            <div>
              <span>{t('LoyaltyV3.RECEIPT_COST')}</span>
              <strong>{formatAmount(totalPrice)} ₸</strong>
            </div>
            <div className="discount">
              <span>{t('LoyaltyV3.IF_CONFIRM_BONUSES')}</span>
              <strong>− {formatAmount(previewDebit)} ₸</strong>
            </div>
            <div className="payable">
              <span>{t('LoyaltyV3.REMAINS_TO_PAY')}</span>
              <strong>{formatAmount(previewFinalPrice)} ₸</strong>
            </div>
          </Styled.V3BonusTotals>

          <Styled.V3ReceiptCaption>
            {t('LoyaltyV3.RECEIPT')} №{chosenReceipt.doc_id} ·{' '}
            {formatAmount(totalPrice)} ₸
          </Styled.V3ReceiptCaption>
          {couponContent}

          {appliedBonusesError && (
            <Styled.ApplyError>{appliedBonusesError}</Styled.ApplyError>
          )}
          {submitOrderError && (
            <Styled.ApplyError>{submitOrderError}</Styled.ApplyError>
          )}
        </Styled.V3BonusContent>

        <Styled.V3BonusFooter>
          {showCodeInput ? (
            <Styled.V3PrimaryButton
              type="button"
              onClick={handleConfirmOrder}
              disabled={
                externalBlocked ||
                value.length !== 6 ||
                !!submitOrderLoading ||
                !!submitOrderResult
              }
            >
              {submitOrderLoading
                ? t('LoyaltySeparated.CONFIRMING')
                : t('LoyaltyV3.CONFIRM_AND_PAY')}
            </Styled.V3PrimaryButton>
          ) : (
            <>
              <Styled.V3PrimaryButton
                type="button"
                onClick={handleApplyBonuses}
                disabled={
                  externalBlocked || previewDebit <= 0 || appliedBonusesLoading
                }
              >
                {t('LoyaltyV3.YES_USE_BONUSES')}
              </Styled.V3PrimaryButton>
              <Styled.V3SecondaryButton
                type="button"
                onClick={handleConfirmOrder}
                disabled={
                  externalBlocked || !!submitOrderLoading || !!submitOrderResult
                }
              >
                {submitOrderLoading
                  ? t('LoyaltySeparated.CONFIRMING')
                  : t('LoyaltyV3.NO_WITHOUT_BONUSES')}
              </Styled.V3SecondaryButton>
            </>
          )}
        </Styled.V3BonusFooter>
      </Styled.V3BonusScreen>
    );
  }

  return (
    <Styled.ApplyBlock>
      <Styled.BlockTitle>
        {t('LoyaltySeparated.CONFIRM_TITLE')}
      </Styled.BlockTitle>
      <Styled.BlockHint style={{ textAlign: 'center', margin: 0 }}>
        {t('LoyaltySeparated.CONFIRM_HINT')}
      </Styled.BlockHint>

      {/*  Итог заказа  */}
      <Styled.OrderSummary>
        <Styled.OrderSummaryTitle>
          {t('LoyaltySeparated.ORDER_SUMMARY')}
        </Styled.OrderSummaryTitle>

        <Styled.OrderSummaryRow>
          <span>{t('LoyaltySeparated.RECEIPT_NUMBER')}</span>
          <span>№{chosenReceipt?.doc_id}</span>
        </Styled.OrderSummaryRow>

        <Styled.OrderSummaryRow>
          <span>{t('LoyaltyPage.TOTAL')}</span>
          <span>{totalPrice} тг</span>
        </Styled.OrderSummaryRow>

        {clientBalance > 0 && (
          <Styled.OrderSummaryRow accent="orange">
            <span>{t('LoyaltySeparated.BONUS_DEBIT')}</span>
            <span>
              −{totalDebit - (isStadaBonusesCredit ? totalStadaDebit : 0)} тг
            </span>
          </Styled.OrderSummaryRow>
        )}

        {/* Списание штадовских бонусов */}
        {hasStadaItems && isStadaBonusesCredit && stadaBalance > 0 && (
          <Styled.OrderSummaryRow accent="orange">
            <span>{t('LoyaltySeparated.STADA_BONUS_DEBIT')}</span>
            <span>−{totalStadaDebit} тг</span>
          </Styled.OrderSummaryRow>
        )}

        <Styled.OrderSummaryRow accent="green">
          <span>{t('LoyaltySeparated.BONUS_CREDIT')}</span>
          <span>+{bonusesToClient} Б</span>
        </Styled.OrderSummaryRow>

        {chosenReceipt?.items?.some((item) => item.stadaPercent > 0) &&
          isStadaBonusesCredit && (
            <Styled.OrderSummaryRow accent="green">
              <span>{t('LoyaltySeparated.BONUS_STADA_CREDIT')}</span>
              <span>+{stadaBonus} Б</span>
            </Styled.OrderSummaryRow>
          )}

        <Styled.OrderSummaryDivider />

        <Styled.OrderSummaryRow large>
          <span>{t('LoyaltySeparated.FINAL_PRICE')}</span>
          <span>{finalPrice} тг</span>
        </Styled.OrderSummaryRow>
      </Styled.OrderSummary>

      {/* Кнопка для списания бонусов */}
      {!showCodeInput && (
        <Styled.ApplyRow>
          <Styled.PrimaryButton
            onClick={handleApplyBonuses}
            disabled={
              appliedBonusesLoading ||
              (clientBalance === 0 &&
                (!isStadaBonusesCredit || stadaBalance === 0))
            }
          >
            {appliedBonusesLoading
              ? t('LoyaltySeparated.SENDING')
              : t('LoyaltyPage.APPLY_BONUSES')}
          </Styled.PrimaryButton>

          {clientBalance === 0 &&
            (!isStadaBonusesCredit || stadaBalance === 0) && (
              <Styled.BlockHint style={{ margin: 0, fontSize: '13px' }}>
                {t('LoyaltySeparated.NO_BONUSES_HINT')}
              </Styled.BlockHint>
            )}
        </Styled.ApplyRow>
      )}

      {/* Ошибка кода */}
      {appliedBonusesError && (
        <Styled.ApplyError>{appliedBonusesError}</Styled.ApplyError>
      )}

      {/* Код клиента для списания бонусов */}
      {showCodeInput && (
        <Styled.CodeInputWrapper>
          <Styled.BlockHint style={{ margin: 0, textAlign: 'center' }}>
            {t('LoyaltySeparated.ENTER_SMS_CODE')}
          </Styled.BlockHint>
          <ReactCodeInput
            fields={6}
            fieldWidth={44}
            fieldHeight={48}
            placeholder={['×', '×', '×', '×', '×', '×']}
            values={[...value]}
            onChange={handleCodeInputChange}
          />
        </Styled.CodeInputWrapper>
      )}

      {/* Подтверждение заказа */}
      <Styled.ApplyRow>
        <Styled.SecondaryButton
          onClick={handleConfirmOrder}
          disabled={!!submitOrderLoading || !!submitOrderResult}
        >
          {submitOrderLoading
            ? t('LoyaltySeparated.CONFIRMING')
            : t('LoyaltyPage.APPLY')}
        </Styled.SecondaryButton>
      </Styled.ApplyRow>

      {/* Ошибка подтверждения заказа */}
      {submitOrderError && (
        <Styled.ApplyError>{submitOrderError}</Styled.ApplyError>
      )}

      {/* Успешное оформление */}
      {submitOrderResult && (
        <Styled.ApplySuccess error={!!sendToStandartError}>
          {sendToStandartError ? '✘' : '✓'}{' '}
          {t(
            `LoyaltySeparated.${
              sendToStandartError ? 'STANDART_ERROR' : 'ORDER_SUCCESS'
            }`,
            {
              title: 'бонусы',
            }
          )}
        </Styled.ApplySuccess>
      )}

      {/* QR для посадки бонусов в стандарт */}
      {qrSrc && submitOrderResult && sendToStandartError && (
        <Styled.QrContainer>
          <Styled.BlockHint style={{ margin: 0, textAlign: 'center' }}>
            {t('LoyaltySeparated.QR_HINT')}
          </Styled.BlockHint>
          <Styled.QrPreviewButton
            type="button"
            onClick={() => setIsQrModalOpen(true)}
          >
            <img alt="QR" width={180} height={180} src={qrSrc} />
          </Styled.QrPreviewButton>
        </Styled.QrContainer>
      )}

      {qrError && <Styled.ApplyError>{qrError}</Styled.ApplyError>}

      {/* увеличенный QR */}
      <Modal open={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
        <Styled.QrModalContent>
          {qrSrc ? <Styled.QrBig alt="QR big" src={qrSrc} /> : null}
        </Styled.QrModalContent>
      </Modal>
    </Styled.ApplyBlock>
  );
};

export default LoyaltyApplyScreen;
